// Plan 2026-09-29 Task 16: pure SVG helpers for the Japan page shared-pot backtest
// (log-scale equity for the pot vs legacy, linear £ for the Japan leg's cumulative P&L).
export interface ChartTick { pos: number; label: string }
export interface SvgChart { viewBox: string; lines: string[]; xTicks: ChartTick[]; yTicks: ChartTick[] }

export const CHART_W = 600
export const CHART_H = 220
const PAD = { l: 48, r: 8, t: 8, b: 20 }

/** Evenly spaced indices into [0, n), always including the first and last. */
export function sampleIndices(n: number, max: number): number[] {
  if (n <= 0) return []
  if (n <= max) return Array.from({ length: n }, (_, i) => i)
  const step = (n - 1) / (max - 1)
  return Array.from({ length: max }, (_, k) => Math.round(k * step))
}

export function gbpShort(v: number): string {
  const a = Math.abs(v)
  const s = v < 0 ? '−' : ''
  if (a >= 1e6) return `${s}£${(a / 1e6).toFixed(1)}m`
  if (a >= 1e3) return `${s}£${(a / 1e3).toFixed(0)}k`
  return `${s}£${a.toFixed(0)}`
}

function build(dates: string[], ys: number[][], idx: number[],
               yTicks: (lo: number, hi: number) => ChartTick[]): SvgChart | null {
  const all = ys.flat()
  if (all.length === 0 || all.some(v => !Number.isFinite(v))) return null
  let lo = Math.min(...all)
  let hi = Math.max(...all)
  if (hi === lo) { hi += 1; lo -= 1 }
  const x = (k: number) => PAD.l + (idx.length > 1 ? k / (idx.length - 1) : 0) * (CHART_W - PAD.l - PAD.r)
  const y = (v: number) => PAD.t + (1 - (v - lo) / (hi - lo)) * (CHART_H - PAD.t - PAD.b)
  const lines = ys.map(s => s.map((v, k) => `${x(k).toFixed(1)},${y(v).toFixed(1)}`).join(' '))
  const xTicks: ChartTick[] = []
  idx.forEach((i, k) => {
    const yr = dates[i].slice(0, 4)
    if (k === 0 || yr !== dates[idx[k - 1]].slice(0, 4)) xTicks.push({ pos: x(k), label: yr })
  })
  return {
    viewBox: `0 0 ${CHART_W} ${CHART_H}`, lines, xTicks,
    yTicks: yTicks(lo, hi).map(t => ({ pos: y(t.pos), label: t.label })),
  }
}

/** Growth multiple (value ÷ first value), log scale, one line per series; ticks ×1, ×2, ×5, ×10 … */
export function equityChart(dates: string[], series: number[][], maxPoints = 300): SvgChart | null {
  const n = dates.length
  if (n < 2 || series.length === 0 || series.some(s => s.length !== n || s.some(v => !(v > 0)))) return null
  const idx = sampleIndices(n, maxPoints)
  const ys = series.map(s => idx.map(i => Math.log10(s[i] / s[0])))
  return build(dates, ys, idx, (lo, hi) => {
    const ticks: ChartTick[] = []
    for (let d = Math.floor(lo); d <= Math.ceil(hi); d++)
      for (const m of [1, 2, 5]) {
        const v = d + Math.log10(m)
        const mult = m * 10 ** d
        if (v >= lo - 1e-9 && v <= hi + 1e-9)
          ticks.push({ pos: v, label: `×${mult >= 1 ? Math.round(mult) : mult.toFixed(mult >= 0.1 ? 1 : 2)}` })
      }
    return ticks
  })
}

/** One £ series (e.g. cumulative contribution), linear; ticks at the low, £0 (when crossed) and the high. */
export function linearChart(dates: string[], values: number[], maxPoints = 300): SvgChart | null {
  const n = dates.length
  if (n < 2 || values.length !== n) return null
  const idx = sampleIndices(n, maxPoints)
  return build(dates, [idx.map(i => values[i])], idx, (lo, hi) => {
    const ticks: ChartTick[] = [{ pos: lo, label: gbpShort(lo) }, { pos: hi, label: gbpShort(hi) }]
    if (lo < 0 && hi > 0) ticks.splice(1, 0, { pos: 0, label: '£0' })
    return ticks
  })
}
